import type { Payment } from '../payment';
import type { Contract, DefaultContractAction } from './types';
import type {
  ContractRole,
  ContractState,
  ContractTemplate,
  ContractType,
  ContractTypeTag,
} from '@paid-ui/enums/contract';
import type { SupercontractCode } from '@paid-ui/enums/supercontract';
import type { SecurePaidSchedule } from '@paid-ui/forms/supercontract/types';
import type { AccountBalance } from '@paid-ui/queries/get-contract-account';
import type { Address } from '@paid-ui/schemas/zod/address';
import type { Attachment, BankGuaranteeAttachment } from '@paid-ui/schemas/zod/attachment';
import type { AssociatedAccount, CostOfWorks, TaxableAmount } from '@paid-ui/types';
import type {
  ContractBreakdown,
  ContractFeeStructure,
  ContractInsuranceItem,
  ContractLoanInfo,
  ContractPrice,
  ContractRequirement,
  ContractResponsibility,
  DelayAllowance,
  LiquidatedDamages,
  PracticalCompletion,
  ReasonableDelays,
} from '@paid-ui/types/contracts';
import type { ContractParticipantParty, TheOtherReviewParty } from '@paid-ui/types/parties';
import type { PaymentSchedule } from '@paid-ui/types/payment-schedule';
import type { RequiringAction } from '@paid-ui/types/requiring-actions';
import type { ContractRetention, RetentionSchedule } from '@paid-ui/types/retentions';
import type { SupercontractTemplate } from '@paid-ui/types/supercontract';
import type { Timeline } from '@paid-ui/types/timeline';
import type { Adjustment, Variation } from '@paid-ui/types/variations';
import type { WorkItem, WorkItemGroup } from '@paid-ui/types/work-items';
import type { Dayjs } from 'dayjs';

import { PaymentTermsType } from '@paid-ui/constants';
import { defaultAddress } from '@paid-ui/constants/address';
import { ClaimMethod } from '@paid-ui/enums/claim';
import { ContractSource } from '@paid-ui/enums/contract';
import { ContractAction } from '@paid-ui/enums/requiring-action';

const taxableAmount = {
  amount: 0,
  excludeGstAmount: 0,
  gstAmount: 0,
} as TaxableAmount;

export const initialState = {
  id: '',
  contractName: '',
  fullContractName: '',
  suburbAndState: '',
  currentContract: null as Contract,
  contractPrice: 0,
  contractState: '' as ContractState | '',
  contractStateText: 'Loading...',
  contractTemplate: '' as ContractTemplate | '',
  templateCode: '' as SupercontractCode | '',
  contractType: '' as ContractType,
  contractAddress: defaultAddress as Address,
  formattedAddress: '',
  center: { lat: -24.15, lng: 133.25 },
  zoom: 4,
  timezone: '',
  descriptionOfWorks: '',
  source: ContractSource.ON_PLATFORM as ContractSource,
  hasAllSigned: false,
  tags: [] as ContractTypeTag[],

  partyName: '',
  currentParty: null as ContractParticipantParty | null,
  theOtherPartyName: '',
  theOtherReviewParty: null as TheOtherReviewParty | null,
  theOtherClaimReviewParty: null as ContractParticipantParty | null,
  theOtherParty: null as ContractParticipantParty | null,

  numberOfParticipants: 0,
  numberOfSignatories: 0,
  numberOfJoined: 0,
  /** Every party but the non-contractual ones (funder, QS) has joined — they never hold the contract back. */
  allContractualPartiesJoined: false,
  numberOfApproved: 0,
  numberOfSigned: 0,

  isPayer: false,
  isPayee: false,
  isPayerHasAccount: false,
  isInviter: false,
  isInvitee: false,
  isSuperintendent: false,
  isArchitect: false,
  isFunder: false,
  isQS: false,
  isNonContractualParty: false,

  payeeParty: null as ContractParticipantParty | null,
  payerParty: null as ContractParticipantParty | null,
  participantParties: [] as ContractParticipantParty[],
  inviteeParty: null as ContractParticipantParty | null,
  inviterParty: null as ContractParticipantParty | null,
  lastReviewParty: null as ContractParticipantParty | null,

  superintendentPartyName: '',
  superintendentParty: null as ContractParticipantParty | null,

  architectName: '',
  architectParty: null as ContractParticipantParty | null,

  payerAgentParty: null as ContractParticipantParty | null,

  funders: [] as ContractParticipantParty[],
  qss: [] as ContractParticipantParty[],

  roles: {} as Partial<Record<ContractRole, ContractResponsibility>>,
  responsibilities: {
    [ContractAction.CONTRACT_REVIEW]: [],
    [ContractAction.CONTRACT_AMEND]: [],
    [ContractAction.CONTRACT_SIGN]: [],
    [ContractAction.CLAIM_SUBMIT]: [],
    [ContractAction.CLAIM_REVIEW]: [],
    [ContractAction.CLAIM_EDIT]: [],
    [ContractAction.CLAIM_ASSESS]: [],
    [ContractAction.CLAIM_PAY]: [],
    [ContractAction.CLAIM_APPROVE]: [],
    [ContractAction.VARIATION_SUBMIT]: [],
    [ContractAction.VARIATION_REVIEW]: [],
    [ContractAction.CLAIM_ASSESSMENT_REQUEST_SUBMIT]: [],
    [ContractAction.CLAIM_ASSESSMENT_REPORT_SUBMIT]: [],
    [ContractAction.RETENTION_REQUEST_UNRETAIN_SUBMIT]: [],
  } as Record<ContractAction, ContractRole[]>,

  lineWorkItems: [] as WorkItem[],
  variationWorkItems: [] as WorkItem[],
  workItems: [] as WorkItem[],
  workItemGroups: [] as WorkItemGroup[],
  numberOfWorkItems: 0,
  numberOfLineWorkItems: 0,
  numberOfVariationWorkItems: 0,
  lineWorkItemsAmount: 0,
  variationWorkItemsAmount: 0,
  workItemsAmount: 0,
  lineWorkItemsAssessedAmount: 0,
  variationWorkItemsAssessedAmount: 0,
  totalAssessedAmount: 0,
  timelines: [] as Timeline[],

  retentions: [] as ContractRetention[],
  retentionSchedule: null as RetentionSchedule | null,
  estimatedRetention: 0,
  retainableInfo: {
    assessed: taxableAmount,
    claimable: taxableAmount,
    retainable: taxableAmount,
    maxRetainable: taxableAmount,
    remainingRetainable: taxableAmount,
  },
  onPlatformRetainable: {
    balance: taxableAmount,
    released: taxableAmount,
    retained: taxableAmount,
    unretained: taxableAmount,
    onboarded: taxableAmount,
    overRetained: taxableAmount,
  },
  offPlatformRetainable: {
    retained: taxableAmount,
  },

  securePaidSchedule: {} as SecurePaidSchedule,

  feeStructure: null as ContractFeeStructure | null,
  loan: null as ContractLoanInfo | null,
  landOwner: '',

  paymentStructure: ClaimMethod.STAGED,
  paymentSchedule: null as PaymentSchedule | null,
  paymentTerms: {
    days: 14,
    type: PaymentTermsType.DEFAULT,
  },

  price: {
    initial: 0,
    current: 0,
    paid: {
      amount: 0,
      percentage: 0,
    },
    remaining: {
      amount: 0,
      percentage: 0,
    },
  } as ContractPrice,

  costOfWorks: {
    isGstFree: true,
    taxableAmount: {
      amount: 0,
      excludeGstAmount: 0,
      gstAmount: 0,
    },
  } as CostOfWorks,

  initialCostOfWorks: {
    isGstFree: true,
    taxableAmount: {
      amount: 0,
      excludeGstAmount: 0,
      gstAmount: 0,
    },
  } as CostOfWorks,

  breakdown: {
    initial: {
      value: 0,
      exclGst: 0,
      inclGst: 0,
    },
    current: {
      value: 0,
      exclGst: 0,
      inclGst: 0,
    },
    provisional: {
      value: 0,
      exclGst: 0,
      inclGst: 0,
    },
    paid: {
      amount: 0,
      percentage: 0,
    },
    remaining: {
      amount: 0,
      percentage: 0,
    },
    variations: {
      total: {
        value: 0,
        exclGst: 0,
        inclGst: 0,
      },
      provisional: {
        value: 0,
        exclGst: 0,
        inclGst: 0,
      },
      approved: {
        value: 0,
        exclGst: 0,
        inclGst: 0,
      },
      unapproved: {
        value: 0,
        exclGst: 0,
        inclGst: 0,
      },
    },
  } as ContractBreakdown,

  payments: [] as Payment[],
  provisionalClaims: [] as Payment[],
  totalNumberOfPayments: 0,
  numberOfClaimedPayments: 0,
  numberOfUnclaimedPayments: 0,
  numberOfProvisionalClaims: 0,

  hasWorkCommenced: false,
  hasWorkCompleted: false,
  commencementDate: null as Dayjs | null,
  contractualCompletionDate: null as Dayjs | null,
  actualCompletionDate: null as Dayjs | null,
  anticipatedCommencementDate: null as Dayjs | null,
  anticipatedCompletionDate: null as Dayjs | null,
  initialConstructionDays: 0,
  constructionDays: 0,
  remainingDays: 0,
  overdueDays: 0,

  gstFree: false,
  gstRate: 10,
  priceIncludeGst: true,

  interestRateForLatePayments: '' as unknown as number,
  builderMargin: 0,
  ownerCausedDelays: '' as unknown as number,
  lateCompletion: '' as unknown as number,
  defectsLiabilityPeriod: 0,

  inclementWeather: 0,
  weekendDays: 0,
  weekdayPublicHolidays: 0,
  rosteredDaysOff: 0,
  foreseeableBreaks: 0,
  builderHolidays: 0,
  additionalDaysFromVariations: 0,
  reasonableDelays: null as ReasonableDelays | null,
  totalTimeExtension: 0,

  insurances: [] as ContractInsuranceItem[],
  requirements: [] as ContractRequirement[],
  isDepositInsuranceRequired: false,
  isDepositInsuranceExists: false,
  depositInsuranceCertificate: null as Attachment | null,
  isContractInsuranceExists: false,
  contractInsuranceCertificate: null as Attachment | null,

  masterContractUrl: '',
  originalContractUrl: '',
  uploadFolderId: '',
  pdfContract: null as Attachment | null,
  notices: [] as Attachment[],
  attachments: [] as Attachment[],
  certificates: [] as Attachment[],
  bankGuarantees: [] as BankGuaranteeAttachment[],
  reports: [] as Attachment[],
  specialConditions: '',
  liquidatedDamages: null as LiquidatedDamages | null,
  delayAllowances: [] as DelayAllowance[],
  practicalCompletions: [] as PracticalCompletion[],

  variations: [] as Variation[],
  adjustments: [] as Adjustment[],
  primeCostAdjustments: [] as Adjustment[],
  provisionalSumAdjustments: [] as Adjustment[],
  variationsAndAdjustments: [] as (Variation | Adjustment)[],
  numberOfVariations: 0,
  numberOfAdjustments: 0,
  numberOfVariationsAndAdjustments: 0,

  requiringActions: [] as RequiringAction[],
  defaultActions: [] as DefaultContractAction[],
  dropdownActions: [] as DefaultContractAction[],

  statuses: {
    isContractAccepted: false,
    isContractClosed: false,
    isContractExecuted: false,
    isContractPostExecuted: false,
    isDraftContract: false,
    isOneLastPaymentLeft: false,
    isOffPlatformContract: false,
    isDigitalOnlyContract: false,
    isPrimeContract: false,
    isSecondaryContract: false,
    isFundingContract: false,
    isServiceContract: false,
    isPeriodic: false,
    isRBNContract: false,
    isSuperContract: false,
    hasChangeFlow: false,
  },

  permissions: {
    canAbandonContract: false,
    canCreateClaim: false,
    canEditClaim: false,
    canReviewClaim: false,
    canAssessClaim: false,
    canSubmitClaim: false,
    canCreateVariation: false,
    canResendInvitation: false,
    canReviewContract: false,
    canSendCommencementNotice: false,
    canSendCompletionNotice: false,
    canSignContract: false,
    canViewContract: false,
    canInviteFunder: false,
    canInviteQS: false,
    canRequestLinkedContractsAccess: false,
    canClaimRetention: false,
    canRevokeContract: false,
    canEditDraftContract: false,
    canRecallContract: false,
    canDeleteDraftContract: false,
    canDepositRefund: false,
    canTerminateContract: false,
    canSecurePayment: false,
    canSecureContract: false,
    canUploadContract: false,
    canChangePrimaryContact: false,
    canUpdateFeeStructure: false,
    canUnjoinContract: false,
    canEditContractSignatories: false,
  },

  paymentRef: '',

  account: {
    id: '',
    balance: {
      name: '',
      accountNumber: '',
      available: 0,
      bsb: '',
      creationTimestamp: '',
      hold: 0,
      reserve: 0,
      uncleared: 0,
      netAvailable: 0,
    } as AccountBalance,
  },

  retentionAccount: null as AssociatedAccount | null,

  snapshot: null as Record<string, any> | null,
  metadata: null as SupercontractTemplate | null,
  additionalData: {} as Record<string, any>,

  forceUpdate: 0,
  loading: false,
};

export type ContractManagerState = typeof initialState;
